// Admin · Webhook Log
// Table of the last N webhook events with expandable per-row field diffs.
// Renders payload + which fields were added / changed / left unchanged / stopped
// being sent — so the ops team can spot silent field-drop bugs before they hurt.
import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Check, ChevronDown, ChevronRight, Filter, RefreshCw, Search, Webhook } from "lucide-react";

import Layout from "../../components/Layout";
import api from "../../lib/api";


const COLLECTIONS = ["products", "orders", "customers", "persons", "calls", "suppliers"];
const STATUSES = [
  { key: "ok",    label: "Success", color: "text-emerald-300 bg-emerald-500/10 border-emerald-400/30" },
  { key: "error", label: "Error",   color: "text-rose-300 bg-rose-500/10 border-rose-400/30" },
];


function relTime(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60)     return `${s}s ago`;
  if (s < 3600)   return `${Math.floor(s / 60)}m ago`;
  if (s < 86400)  return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}


function StatusPill({ status }) {
  const cfg = STATUSES.find((x) => x.key === status);
  if (!cfg) return <span className="text-slate-400 text-xs">{status}</span>;
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-mono uppercase tracking-widest ${cfg.color}`}
      data-testid={`webhook-log-status-${status}`}
    >
      {status === "ok"
        ? <Check className="w-2.5 h-2.5" strokeWidth={3} />
        : <AlertTriangle className="w-2.5 h-2.5" strokeWidth={2.5} />}
      {cfg.label}
    </span>
  );
}


/**
 * Diff renderer — shows every field the payload touched, grouped by outcome:
 * added / changed / unchanged, plus a warning list of fields that USED TO exist
 * but are no longer being sent by the upstream system (silent-drop detection).
 */
function DiffView({ diff, payload, error }) {
  if (!diff && error) {
    return (
      <div className="p-4 rounded-lg border border-rose-500/30 bg-rose-500/[0.04]">
        <div className="text-[10px] font-mono uppercase tracking-widest text-rose-300 mb-1">Error</div>
        <code className="text-[12px] text-rose-200 break-all">{error}</code>
        {payload && (
          <>
            <div className="text-[10px] font-mono uppercase tracking-widest text-rose-300 mt-4 mb-1">Payload that failed</div>
            <pre className="text-[11px] text-slate-300 bg-black/30 rounded p-3 max-h-72 overflow-auto whitespace-pre-wrap break-all">
              {JSON.stringify(payload, null, 2)}
            </pre>
          </>
        )}
      </div>
    );
  }
  if (!diff) return <div className="text-slate-500 text-xs">No diff available.</div>;

  const { added = [], changed = [], unchanged = [], present_on_old_only = [], was_new_doc } = diff;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {/* Added */}
      <div className="rounded-lg border border-emerald-500/25 bg-emerald-500/[0.03] p-3">
        <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-300 mb-2">
          Added ({added.length}){was_new_doc && " · new doc"}
        </div>
        {added.length === 0 ? (
          <div className="text-slate-500 text-xs italic">No new fields.</div>
        ) : (
          <ul className="space-y-1">
            {added.map((f) => (
              <li key={`add-${f.field}`} className="text-[12px]">
                <span className="font-mono text-emerald-300">{f.field}</span>
                <span className="text-slate-500 mx-1.5">→</span>
                <code className="text-slate-200 break-all">{formatValue(f.after)}</code>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Changed */}
      <div className="rounded-lg border border-amber-500/25 bg-amber-500/[0.03] p-3">
        <div className="text-[10px] font-mono uppercase tracking-widest text-amber-300 mb-2">
          Changed ({changed.length})
        </div>
        {changed.length === 0 ? (
          <div className="text-slate-500 text-xs italic">No fields changed.</div>
        ) : (
          <ul className="space-y-1.5">
            {changed.map((f) => (
              <li key={`chg-${f.field}`} className="text-[12px]">
                <div className="font-mono text-amber-300">{f.field}</div>
                <div className="ml-3 text-[11px]">
                  <span className="text-rose-400">before:</span>{" "}
                  <code className="text-slate-400 line-through break-all">{formatValue(f.before)}</code>
                </div>
                <div className="ml-3 text-[11px]">
                  <span className="text-emerald-400">after: </span>{" "}
                  <code className="text-slate-100 break-all">{formatValue(f.after)}</code>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Unchanged */}
      <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-2">
          Unchanged ({unchanged.length})
        </div>
        {unchanged.length === 0 ? (
          <div className="text-slate-500 text-xs italic">Nothing left as-is.</div>
        ) : (
          <div className="flex flex-wrap gap-1">
            {unchanged.map((f) => (
              <span key={`unc-${f.field}`} className="font-mono text-[11px] text-slate-500 border border-white/[0.06] rounded px-1.5 py-0.5">
                {f.field}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Present on old only — silent-drop detector */}
      <div className="rounded-lg border border-rose-500/25 bg-rose-500/[0.03] p-3">
        <div className="text-[10px] font-mono uppercase tracking-widest text-rose-300 mb-2 flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" />
          Upstream stopped sending ({present_on_old_only.length})
        </div>
        {present_on_old_only.length === 0 ? (
          <div className="text-slate-500 text-xs italic">All previously-known fields are still coming through.</div>
        ) : (
          <>
            <div className="text-[10.5px] text-rose-200/80 mb-2">
              These fields exist on the stored doc but weren&apos;t in the latest payload — upstream stopped sending them (or they&apos;ll silently persist as stale data).
            </div>
            <div className="flex flex-wrap gap-1">
              {present_on_old_only.map((f) => (
                <span key={`drop-${f}`} className="font-mono text-[11px] text-rose-300 border border-rose-500/30 rounded px-1.5 py-0.5">
                  {f}
                </span>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}


function formatValue(v) {
  if (v === null) return "null";
  if (v === undefined) return "undefined";
  if (typeof v === "object") {
    try {
      const s = JSON.stringify(v);
      return s.length > 120 ? s.slice(0, 120) + "…" : s;
    } catch { return String(v); }
  }
  const s = String(v);
  return s.length > 200 ? s.slice(0, 200) + "…" : s;
}


function Row({ entry, isOpen, onToggle }) {
  return (
    <>
      <tr
        onClick={onToggle}
        data-testid={`webhook-log-row-${entry._id}`}
        className={`border-b border-white/[0.04] cursor-pointer transition-colors ${
          isOpen ? "bg-white/[0.03]" : "hover:bg-white/[0.02]"
        }`}
      >
        <td className="px-3 py-2 text-slate-400">
          {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        </td>
        <td className="px-3 py-2 text-[11.5px] text-slate-300 whitespace-nowrap" title={entry.ts}>
          {relTime(entry.ts)}
        </td>
        <td className="px-3 py-2">
          <span className="font-mono text-[11px] uppercase tracking-widest text-teal-300">
            {entry.collection}
          </span>
        </td>
        <td className="px-3 py-2 font-mono text-[11.5px] text-slate-300 break-all">
          {entry.doc_id}
        </td>
        <td className="px-3 py-2">
          <span className="text-[10.5px] font-mono uppercase tracking-widest text-slate-400">
            {entry.action}
          </span>
        </td>
        <td className="px-3 py-2"><StatusPill status={entry.status} /></td>
        <td className="px-3 py-2 text-[10.5px] font-mono text-slate-500">
          {entry.error_id || ""}
        </td>
      </tr>
      {isOpen && (
        <tr className="bg-black/20">
          <td colSpan={7} className="px-4 py-4">
            <DiffView diff={entry.diff} payload={entry.payload} error={entry.error} />
            <details className="mt-4">
              <summary className="cursor-pointer text-[10.5px] font-mono uppercase tracking-widest text-slate-500 hover:text-slate-300">
                Raw payload ({Object.keys(entry.payload || {}).length} keys)
              </summary>
              <pre className="mt-2 text-[11px] text-slate-300 bg-black/30 rounded p-3 max-h-72 overflow-auto whitespace-pre-wrap break-all">
                {JSON.stringify(entry.payload, null, 2)}
              </pre>
            </details>
          </td>
        </tr>
      )}
    </>
  );
}


export default function WebhookLog() {
  const [items, setItems] = useState([]);
  const [facets, setFacets] = useState({ collections: {}, statuses: {} });
  const [collection, setCollection] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [openRow, setOpenRow] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchLog = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "100" });
      if (collection) params.set("collection", collection);
      if (status)     params.set("status", status);
      if (search.trim()) params.set("search", search.trim());
      const { data } = await api.get(`/admin/webhook-log?${params.toString()}`);
      setItems(data.items || []);
      setFacets(data.facets || { collections: {}, statuses: {} });
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn("webhook-log fetch failed", err);
    } finally {
      setLoading(false);
    }
  }, [collection, status, search]);

  useEffect(() => { fetchLog(); }, [fetchLog]);

  const errorCount = facets.statuses?.error || 0;
  const okCount = facets.statuses?.ok || 0;

  return (
    <Layout>
      <div className="space-y-6" data-testid="admin-webhook-log">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-amber-300 font-mono mb-1 flex items-center gap-1.5">
              <Webhook className="w-3 h-3" /> Admin · Live Webhook Feed
            </div>
            <h1 className="text-3xl font-display font-bold tracking-tight">Webhook Log</h1>
            <p className="text-slate-400 text-sm mt-1 max-w-3xl">
              Last 100 inbound webhook events with computed field diffs. Every row shows
              which fields upstream <span className="text-emerald-300">added</span>,{" "}
              <span className="text-amber-300">changed</span>, left{" "}
              <span className="text-slate-400">unchanged</span>, and — most importantly —{" "}
              <span className="text-rose-300">stopped sending</span>. That last column is
              your silent-drop alarm.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-[10.5px] font-mono uppercase tracking-widest text-slate-500">
              Total logged
            </div>
            <div className="flex items-center gap-2 text-[13px]">
              <span className="text-emerald-300 font-mono">{okCount} ok</span>
              <span className="text-slate-600">·</span>
              <span className="text-rose-300 font-mono">{errorCount} errors</span>
            </div>
            <button
              type="button"
              onClick={fetchLog}
              disabled={loading}
              data-testid="webhook-log-refresh"
              className="ml-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/10 bg-white/[0.03] text-sm text-slate-300 hover:text-white hover:bg-white/[0.06] disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 flex-wrap border border-white/[0.06] bg-white/[0.02] rounded-xl p-3">
          <div className="flex items-center gap-1.5 text-[10.5px] font-mono uppercase tracking-widest text-slate-500">
            <Filter className="w-3 h-3" /> Filter
          </div>

          <select
            value={collection}
            onChange={(e) => setCollection(e.target.value)}
            data-testid="webhook-log-collection-filter"
            className="bg-black/30 border border-white/10 rounded px-2 py-1 text-sm text-slate-200"
          >
            <option value="">All collections ({items.length})</option>
            {COLLECTIONS.map((c) => (
              <option key={c} value={c}>
                {c} ({facets.collections?.[c] || 0})
              </option>
            ))}
          </select>

          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            data-testid="webhook-log-status-filter"
            className="bg-black/30 border border-white/10 rounded px-2 py-1 text-sm text-slate-200"
          >
            <option value="">Any status</option>
            <option value="ok">Success only ({okCount})</option>
            <option value="error">Errors only ({errorCount})</option>
          </select>

          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search doc_id, error_id, error message…"
              data-testid="webhook-log-search"
              className="w-full bg-black/30 border border-white/10 rounded pl-8 pr-2 py-1 text-sm text-slate-200 placeholder-slate-500"
            />
          </div>
        </div>

        {/* Table */}
        <div className="border border-white/[0.06] rounded-xl overflow-hidden">
          <table className="w-full text-[12.5px]">
            <thead className="bg-white/[0.03] border-b border-white/[0.06]">
              <tr className="text-left text-[10px] font-mono uppercase tracking-widest text-slate-500">
                <th className="px-3 py-2 w-8"></th>
                <th className="px-3 py-2 w-24">When</th>
                <th className="px-3 py-2 w-28">Collection</th>
                <th className="px-3 py-2">Doc ID</th>
                <th className="px-3 py-2 w-24">Action</th>
                <th className="px-3 py-2 w-28">Status</th>
                <th className="px-3 py-2 w-24">Error ID</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && !loading && (
                <tr>
                  <td colSpan={7} className="px-4 py-16 text-center text-slate-500">
                    No webhook events logged yet. They&apos;ll appear here the moment upstream sends one.
                  </td>
                </tr>
              )}
              {items.map((entry) => (
                <Row
                  key={entry._id}
                  entry={entry}
                  isOpen={openRow === entry._id}
                  onToggle={() => setOpenRow(openRow === entry._id ? null : entry._id)}
                />
              ))}
            </tbody>
          </table>
        </div>

        <div className="text-[10.5px] text-slate-600 font-mono">
          Retention: last ~10,000 events (auto-trimmed on boot). Log ships to Raj@talmia.com only on write failures.
        </div>
      </div>
    </Layout>
  );
}
